import { Fragment, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Backdrop, Box, CircularProgress, Grid } from "@mui/material";
import { TitleAndActionBox } from "../../../common";
import { LanguageContext } from "../../../../context/LanguageContext";
import { useNavigate, useLocation } from "react-router-dom";
import { ParagraphText, UBButton, UBInput } from "../../../../Libraries";
import TabAutoComplete from "../../../../Libraries/TabAutoComplete";
import styles from "./SendMoney.module.css";
import formStyles from "../../SaleExpense/SaleExpenseForm/styles.module.css";
import {
  BankIconNew,
  EnterIcon,
  ModalIcon,
} from "../../../../assets/icons/icon";
import VerifyPinModal from "./VerifyPinModal";
import { formatNum } from "../../../../oscar-pos-core/constants";
import { analytics, triggerLogEvent } from "../../../../config/firebase";
import { useSnackbar } from "../../../../context/SnackbarContext";
import { useWalletContext } from "../../../../context/WalletContext";
import axios from "axios";
import { handleApi } from "../../../../apis";
import { TITLEFETCH } from "../../../../apis/configuration";
import { getCategoriesByType } from "../../../../utils";
import { v4 as uuidv4 } from "uuid";
import { SelectItemModal } from "../../../common/Modals";
import BusinessLoader from "../../../common/BusinessLoader";
import SearchSkeleton from "../../../common/SearchSkeleton";
import AppCollectPayment from "../../../common/AppCollectPayment";
import { camelCase, EMIONBOARDINGSTATUS, sentenceCase } from "../../../../constants";

/**
 * Input type detection rules:
 * - iban    : starts with "PK" and length >= 20
 * - phone   : exactly 11 digits starting with "03"
 * - account : 6+ digits (not phone, not iban)
 * - name    : everything else (has letters / short)
 */
const detectInputType = (value) => {
  if (!value || !value.trim()) return "none";
  const v = value.trim();
  if (v.toUpperCase().startsWith("PK") && v.length >= 20) return "iban";
  if (/^03\d{9}$/.test(v)) return "phone";
  if (/^\d{6,}$/.test(v)) return "account";
  return "name";
};

const SendMoney = () => {
  const [I18n] = useContext(LanguageContext);
  const location = useLocation();
  const expensePayload = location.state?.expensePayload || null;
  const invoiceUpdatePayload = location.state?.invoiceUpdatePayload || null;
  const backPath = location.state?.backPath || null;
  const isAmountLocked =
    Boolean(expensePayload?.amount) ||
    Boolean(invoiceUpdatePayload?.amountToSend);

  const [state, setState] = useState({
    bankName: "",
    bankLabel: "",
    accountNumber: "",
    category: "",
    categoryId: "",
    notes: "",
    amount: expensePayload?.amount
      ? String(expensePayload.amount)
      : invoiceUpdatePayload?.amountToSend
        ? String(invoiceUpdatePayload.amountToSend)
        : "",
  });

  const [categoryList, setCategoryList] = useState([]);

  const idempotencyKey = useRef(uuidv4());

  const navigate = useNavigate();
  const [isDetailView, setIsDetailView] = useState(false);
  const [isVerifyOpen, setIsVerifyOpen] = useState(false);
  const [isBankModalOpen, setIsBankModalOpen] = useState(false);
  const [accountDetail, setAccountDetail] = useState({});

  // Inline skeleton (phone title fetch / IBAN fetch)
  const [inlineLoading, setInlineLoading] = useState(false);
  // Inline title fetch results array
  const [inlineFetchResults, setInlineFetchResults] = useState([]);
  // Full-screen BusinessLoader (account/phone "Select bank" flow)
  const [bankSelectLoading, setBankSelectLoading] = useState(false);

  const {
    banks,
    walletData,
    fetchBankList,
    bankIsLoading,
    fetchBeneficiaryList,
    beneficiaryList,
    isBeneficiaryListLoading,
    handleGetWalletBalance,
    fetchAccountBalanceLoading,
  } = useWalletContext();


  const balance = walletData?.current_balance || 0;
  const isUnSufficiantBalance = Number(state.amount) > balance;
  const isDisbaleAccountDetail =
    !(state.amount && !isUnSufficiantBalance) || Number(state.amount) === 0;
  const { showSnackbar } = useSnackbar();

  const inputType = detectInputType(state.accountNumber);
  // Show "Select the bank" row when 6+ digits (account or phone), never on IBAN
  const showSelectBankRow =
    (inputType === "account" || inputType === "phone") &&
    state.accountNumber.length >= 6;

  const allBeneficiaries = useMemo(
    () => beneficiaryList?.accounts || beneficiaryList || [],
    [beneficiaryList],
  );

  const filteredBeneficiaries = useMemo(() => {
    const raw = state.accountNumber || "";
    const q = raw.trim().replace(/\s+/g, " ").toLowerCase();
    if (!q) return allBeneficiaries;
    return allBeneficiaries.filter(
      (item) =>
        (item.account_title || "").toLowerCase().includes(q) ||
        (item.account_number || "").includes(raw) ||
        (item.iban || "").toLowerCase().includes(q) ||
        (item.raast_id || "").includes(raw),
    );
  }, [state.accountNumber, allBeneficiaries]);


  useEffect(() => {
    if (!isBeneficiaryListLoading && walletData?.account_info?.emi_onboarding_status == EMIONBOARDINGSTATUS.VERIFIED) fetchBeneficiaryList(navigate);
    if (!banks || banks?.length == 0) {
      fetchBankList(navigate);
    }
    setCategoryList(getCategoriesByType("send_money"));
  }, []);

  useEffect(() => {
    if (isDetailView) {
      handleGetWalletBalance(navigate);
    }
  }, [isDetailView]);

  useEffect(() => {
    idempotencyKey.current = uuidv4();
  }, [state.amount]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Auto title fetch for phone and IBAN ──────────────────────────────────
  useEffect(() => {
    setInlineFetchResults([]);

    if (inputType === "phone") {
      setInlineLoading(true);
      const cancelSource = axios.CancelToken.source();
      const timer = setTimeout(async () => {
        try {
          const res = await handleApi(
            TITLEFETCH,
            { bank: "", account: state.accountNumber },
            cancelSource,
            navigate,
          );
          setInlineFetchResults(res?.accounts || []);
        } catch (err) {
          if (!axios.isCancel(err)) setInlineFetchResults([]);
        } finally {
          setInlineLoading(false);
        }
      }, 400);
      return () => {
        clearTimeout(timer);
        cancelSource.cancel();
      };
    }

    if (
      inputType === "iban" &&
      filteredBeneficiaries.length === 0 &&
      state.accountNumber
    ) {
      setInlineLoading(true);
      const cancelSource = axios.CancelToken.source();
      const timer = setTimeout(async () => {
        try {
          const res = await handleApi(
            TITLEFETCH,
            { bank: "", account: state.accountNumber },
            cancelSource,
            navigate,
          );
          setInlineFetchResults(res?.accounts || []);
        } catch (err) {
          if (!axios.isCancel(err)) setInlineFetchResults([]);
        } finally {
          setInlineLoading(false);
        }
      }, 700);
      return () => {
        clearTimeout(timer);
        cancelSource.cancel();
      };
    }

    setInlineLoading(false);
  }, [state.accountNumber, inputType, filteredBeneficiaries.length]);

  const onSendMoney = () => {
    if (state.amount) triggerLogEvent(analytics, "w_send_amount");
    triggerLogEvent(analytics, "w_send_send_money");
    setIsVerifyOpen(true);
  };

  // Called when user selects a bank from the modal (account / phone flow)
  const handleBankSelect = async (bank) => {
    triggerLogEvent(analytics, "w_send_select_bank");
    setState((e) => ({ ...e, bankName: bank.value, bankLabel: bank.label }));
    if (!state.accountNumber) return;
    setBankSelectLoading(true);
    try {
      const res = await handleApi(
        TITLEFETCH,
        { bank: bank.name, account: state.accountNumber },
        undefined,
        navigate,
      );
      const data = res?.accounts?.[0];
      if (data) {
        triggerLogEvent(analytics, "w_send_details_fetched_success");
        idempotencyKey.current = uuidv4();
        setAccountDetail({
          accountTitle: data.account_title,
          accountNumber: data.account_number,
          bankLabel: data.bank_details?.display_name,
          bankName: data.bank_details?.name,
          image: data.bank_details?.logo,
          iban: data.iban,
        });
        setIsDetailView(true);
      } else {
        triggerLogEvent(analytics, "w_send_details_fetched_fail");
        showSnackbar({
          message:
            I18n.account_not_found_please_enter_valid_account_number_or_IBAN,
          severity: "error",
          icon: "",
        });
      }
    } catch (err) {
      triggerLogEvent(analytics, "w_send_details_fetched_fail");
      showSnackbar({
        message:
          err?.response?.data?.detail ||
          err?.response?.data?.message ||
          I18n.account_not_found_please_enter_valid_account_number_or_IBAN,
        severity: "error",
        icon: "",
      });
    } finally {
      setBankSelectLoading(false);
    }
  };

  const handleBeneficiarySelect = (item) => {
    const identifier = item.iban || item.account_number || item.raast_id || "";
    const bankName =
      item.bank_details?.display_name || item.raast_display_name || "";
    const logoUrl = item.bank_details?.logo || item.raast_logo_link || "";
    triggerLogEvent(analytics, "w_send_beneficiary_select");
    idempotencyKey.current = uuidv4();
    setAccountDetail({
      accountTitle: item.account_title,
      accountNumber: item.account_number || item.raast_id || "",
      bankLabel: bankName,
      bankName: item.bank_details?.name || "",
      image: logoUrl,
      iban: item.iban,
    });
    setState((e) => ({ ...e, accountNumber: identifier }));
    setIsDetailView(true);
  };

  const handleInlineResultSelect = (data) => {
    idempotencyKey.current = uuidv4();
    setAccountDetail({
      accountTitle: data.account_title,
      accountNumber: data.account_number,
      bankLabel: data.bank_details?.display_name,
      bankName: data.bank_details?.name,
      image: data.bank_details?.logo,
      iban: data.iban,
    });
    setIsDetailView(true);
  };

  const bankList = useMemo(
    () =>
      (banks || []).map((b) => ({
        label: b.display_name || "",
        value: (b.display_name || b.name || "").trim(),
        name: b.name || "",
        img: b.image ? (
          <img src={b.image} alt="" width={20} height={20} />
        ) : null,
      })),
    [banks],
  );

  const renderBeneficiaryRow = (item, key, onClick) => {
    const identifier = item.iban || item.account_number || item.raast_id || "";
    const last4 = identifier.slice(-4);
    const bankName =
      item.bank_details?.display_name || item.raast_display_name || "";
    const logoUrl = item?.raast_id ? item?.raast_logo_link : item.bank_details?.logo || item.raast_logo_link || "";
    const initials = (item.account_title || "?").charAt(0).toUpperCase();
    return (
      <Box id={`send_money_contact_${identifier || key}`} key={key} className={styles.beneficiaryItem} onClick={onClick}>
        <Box className={styles.beneficiaryLeft}>
          <Box className={styles.beneficiaryAvatar}>
            {logoUrl ? (
              <img
                src={logoUrl}
                alt={bankName}
                className={styles.beneficiaryAvatarImg}
              />
            ) : (
              <span className={styles.beneficiaryAvatarInitial}>
                {initials}
              </span>
            )}
          </Box>
          <ParagraphText
            className={`newFontColor fontSize14 ${styles.beneficiaryName}`}
          >
            {item.account_title}
          </ParagraphText>
        </Box>
        <ParagraphText
          className={`newFontColor fontSize14 ${styles.beneficiaryBank}`}
        >
          {bankName}
          {last4 ? ` *${last4}` : ""}
        </ParagraphText>
      </Box>
    );
  };

  const renderInlineResults = () => {
    // No input → show full "Recent" list
    if (!state.accountNumber) {
      if (!allBeneficiaries.length) return null;
      return (
        <Box className={styles.beneficiaryContainer}>
          <ParagraphText className={styles.beneficiaryLabel}>
            {I18n.recent}
          </ParagraphText>
          <Box className={styles.beneficiaryList}>
            {allBeneficiaries.map((item, i) =>
              renderBeneficiaryRow(item, i, () =>
                handleBeneficiarySelect(item),
              ),
            )}
          </Box>
        </Box>
      );
    }

    const dedupedFetchResults = inlineFetchResults.filter((data) => {
      const acct = data.account_number || "";
      const iban = data.iban || "";
      return !filteredBeneficiaries.some(
        (b) =>
          (acct && (b.account_number === acct || b.iban === acct)) ||
          (iban && (b.iban === iban || b.account_number === iban)),
      );
    });

    const showNonIdeal =
      inputType === "name" &&
      /[a-zA-Z]/.test(state.accountNumber) &&
      filteredBeneficiaries.length === 0 &&
      !(state.accountNumber || "").toUpperCase().startsWith("PK");

    const hasContent =
      inlineLoading ||
      filteredBeneficiaries.length > 0 ||
      dedupedFetchResults.length > 0 ||
      showSelectBankRow ||
      showNonIdeal;

    if (!hasContent) return null;
    return (
      <Box
        className={`${styles.beneficiaryContainer} ${styles.inlineSearchBox}`}
      >
        {inlineLoading ? (
          <SearchSkeleton />
        ) : (
          <>
            {filteredBeneficiaries.map((item, i) =>
              renderBeneficiaryRow(item, i, () =>
                handleBeneficiarySelect(item),
              ),
            )}

            {dedupedFetchResults.map((data, i) =>
              renderBeneficiaryRow(data, `fetch-${i}`, () =>
                handleInlineResultSelect(data),
              ),
            )}

            {showSelectBankRow && (
              <Box
                id="send_money_select_bank_row"
                className={`${styles.beneficiaryItem} ${styles.selectBankRow}`}
                onClick={() => setIsBankModalOpen(true)}
              >
                <Box className={styles.beneficiaryLeft}>
                  <Box
                    className={`${styles.beneficiaryAvatar} ${styles.selectBankAvatar}`}
                  >
                    <BankIconNew />
                  </Box>
                  <ParagraphText
                    className={`newFontColor fontSize14 ${styles.beneficiaryName}`}
                  >
                    {I18n.select_start_bank}
                  </ParagraphText>
                </Box>
                <Box className={styles.beneficiaryRight}>
                  <ModalIcon width={20} height={20} />
                </Box>
              </Box>
            )}

            {showNonIdeal && (
              <Box className={styles.noResultBox}>
                <ParagraphText className="fontSize14 newFontColor">
                  {I18n.no_results_enter_complete_account_or_iban}
                </ParagraphText>
              </Box>
            )}
          </>
        )}
      </Box>
    );
  };

  return (
    <Fragment>
      <Grid
        container
        className={`${formStyles.root} ${formStyles.SaleExpenseFormWrapper} ${styles.sendMoneyWrapper} sendMoneyScreen`}
      >
        <Grid item md={12}>
          <TitleAndActionBox
            data={{
              title: I18n.send_money,
              isBack: true,
              onBack: bankIsLoading
                ? () => { }
                : isDetailView
                  ? () => setIsDetailView(false)
                  : () => navigate(-1),
              id: "send_money",
            }}
          />

          <Box className={formStyles.formBodyGrid}>
            <Box className={formStyles.formColumn}>
              <Box
                className={`${formStyles.formBody} expenseFormBody ${styles.sendMoneyFormBody}`}
              >
                {isDetailView ? (
                  <Fragment>
                    <Box
                      className={styles.accountDetailBox}
                      onKeyDown={(e) => {
                        if (
                          e.key === "Enter" &&
                          !isDisbaleAccountDetail &&
                          !fetchAccountBalanceLoading
                        )
                          onSendMoney();
                      }}
                    >
                      {/* Beneficiary summary card */}
                      <Box className={styles.beneficiarySummaryCard}>
                        <Box className={styles.beneficiarySummaryAvatar}>
                          {accountDetail.image ? (
                            <img
                              src={accountDetail.image}
                              alt=""
                              className={styles.beneficiarySummaryLogo}
                            />
                          ) : (
                            <span className={styles.beneficiarySummaryInitial}>
                              {(accountDetail.accountTitle || "?")
                                .charAt(0)
                                .toUpperCase()}
                            </span>
                          )}
                        </Box>
                        <Box>
                          <ParagraphText className="fontSize14 fontWeight500 newFontColor">
                            {accountDetail.accountTitle}
                          </ParagraphText>
                          <ParagraphText className="fontSize13 newLightColor">
                            {accountDetail.iban || accountDetail.accountNumber}
                          </ParagraphText>
                        </Box>
                      </Box>

                      <Box className={formStyles.expenseAmountInputBox}>
                        <UBInput
                          inputId="send_money_amount_input"
                          baseClassname={`w-100 ${styles.sendAmountMoneyField}`}
                          placeholder={I18n.enter_amount}
                          disabled={fetchAccountBalanceLoading}
                          onlyNumbers
                          onChange={(_, value) => {
                            if (isAmountLocked) return;
                            if (
                              (value || "").length >= 1 &&
                              (state.amount || "").length == 0
                            )
                              triggerLogEvent(analytics, "w_send_amount_start");
                            setState((e) => ({ ...e, amount: value }));
                          }}
                          label={sentenceCase(I18n.enter_amount)}
                          required={true}
                          name={"amount"}
                          value={state.amount}
                          error={{
                            text: sentenceCase(I18n.insufficient_balance),
                            show: isUnSufficiantBalance,
                          }}
                          infoLabel={
                            <ParagraphText className={"fontSize12 newLightColor"}>
                              {I18n.available_balance}:{" "}
                              {fetchAccountBalanceLoading ? (
                                <Box
                                  component="span"
                                  className={styles.balanceSkeleton}
                                />
                              ) : (
                                <span className="newLightColor">
                                  {`Rs. ${formatNum(Math.floor(balance || 0))}`}
                                </span>
                              )}
                            </ParagraphText>
                          }
                        />
                      </Box>

                      {isUnSufficiantBalance && !fetchAccountBalanceLoading && (
                        <Box className={styles.insufficientBalanceContainer}>
                          <AppCollectPayment
                            status="Paid"
                            isSale={true}
                            visible={isUnSufficiantBalance && !fetchAccountBalanceLoading}
                            customMessage={I18n.top_up_account_to_complete_transaction}
                            customLinkText={sentenceCase(I18n.add_money)}
                            onGetQR={() => navigate("/UCash/LoadMoney")}
                          />
                        </Box>
                      )}

                      {/* Category */}
                      <TabAutoComplete
                        id="send_money_category"
                        list={categoryList}
                        label={I18n.category}
                        handleSelect={(item) => {
                          triggerLogEvent(analytics, "w_send_category");
                          setState((e) => ({
                            ...e,
                            category: item?.label || "",
                            categoryId: item?.id || "",
                          }))
                        }}
                        value={state.category}
                        placeholder={I18n.select_category}
                        handleStartLogEvent={() => { }}
                        handleManuallyInput={() => { }}
                        manuallyFocusInput={false}
                      />

                      {/* Notes */}
                      <UBInput
                        inputId="send_money_notes_input"
                        label={I18n.notes}
                        onChange={(_, value) => {
                          if (
                            (value || "").length >= 1 &&
                            (state.notes || "").length == 0
                          )
                            triggerLogEvent(analytics, "w_send_notes_start");
                          setState((e) => ({ ...e, notes: value }));
                        }}
                        placeholder={I18n.add_note_here}
                        value={state.notes}
                        name={"notes"}
                        maxLength={100}
                      />
                    </Box>
                  </Fragment>
                ) : (
                  <Fragment>
                    <Box className={styles.fieldsStack}>
                      <UBInput
                        inputId="send_money_account_input"
                        label={`${I18n.account_details}`}
                        onChange={(_, value) => {
                          if (
                            (value || "").length >= 1 &&
                            (state.accountNumber || "").length == 0
                          )
                            triggerLogEvent(analytics, "w_send_account_start");
                          const noSpaces = (value || "").replace(/\s/g, "");
                          const isNameSearch = /[a-zA-Z]/.test(noSpaces.replace(/^pk/i, ""));
                          setState((e) => ({ ...e, accountNumber: isNameSearch ? value : noSpaces }));
                        }}
                        maxLength={34}
                        placeholder={
                          I18n.enter_iban_account_raastid_name_or_phone
                        }
                        required
                        value={state.accountNumber}
                        name={"accountNumber"}
                        autoFocus
                      />
                    </Box>
                    {renderInlineResults()}
                  </Fragment>
                )}
                {isDetailView && (
                  <UBButton
                    id="send_money_submit"
                    title={<ParagraphText>{I18n.next}</ParagraphText>}
                    iconComponent={<EnterIcon />}
                    iconPlacement="right"
                    disabled={
                      isDisbaleAccountDetail || fetchAccountBalanceLoading
                    }
                    className={"buttonPrimary saveSaleButton"}
                    onClick={onSendMoney}
                  />
                )}
              </Box>
            </Box>
          </Box>
        </Grid>
      </Grid>

      <Backdrop
        sx={(theme) => ({ color: "#fff", zIndex: theme.zIndex.drawer + 1 })}
        open={bankIsLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      <VerifyPinModal
        open={isVerifyOpen}
        handleClose={() => setIsVerifyOpen(false)}
        amount={state.amount}
        accountDetail={accountDetail}
        expensePayload={expensePayload}
        invoiceUpdatePayload={invoiceUpdatePayload}
        backPath={backPath}
        categoryId={state.categoryId}
        notes={state.notes}
        idempotencyKey={idempotencyKey.current}
      />

      <SelectItemModal
        id="select_bank_modal"
        open={isBankModalOpen}
        onClose={() => setIsBankModalOpen(false)}
        items={bankList}
        onSelect={handleBankSelect}
        title={I18n.select_start_bank}
        searchPlaceholder={I18n.search_bank}
        noResultText={I18n.no_banks_found}
      />

      {/* Full-screen loader for account/phone → bank select → title fetch */}
      <BusinessLoader loadBusiness={bankSelectLoading} />
    </Fragment>
  );
};

export default SendMoney;
